import { Styleguide, PaletteConfig } from '../utils/s3';
import { sanitizeCssColor, sanitizeCssIdentifier, sanitizeCssLength, sanitizeCssNumber, sanitizeFontFamily } from '../utils/sanitize/css';
import { resolveFontFaceCss } from '../utils/fonts';

interface StyleguideStylesProps {
  styleguide: Styleguide | null;
  /**
   * Inclui o `@font-face` do styleguide no mesmo `<style>`.
   *
   * Existe para o App Router, onde o `DynamicFonts` não serve (usa `next/head`). Fica
   * desligado por padrão porque no Pages Router quem injeta as fontes é o `DynamicFonts`,
   * via `_app.tsx` — ligar aqui duplicaria as declarações.
   */
  includeFontFace?: boolean;
}

/**
 * Mapeia o nome da paleta do JSON para o nome da classe CSS
 * "Paleta 1" -> "palette1", "Paleta 2" -> "palette2", etc.
 */
function getPaletteClassName(paletteName: string): string {
  const match = paletteName.match(/Paleta\s*(\d+)/i);
  if (match) {
    return `palette${match[1]}`;
  }
  // Fallback: remove espaços e converte para lowercase
  return sanitizeCssIdentifier(paletteName.toLowerCase());
}

/**
 * Gera CSS para tipografias
 */
function generateTypographiesCSS(styleguide: Styleguide): string {
  const { typographies } = styleguide;

  const formatFontFamily = (fontFamily: string) => {
    // Manter lowercase para match com o @font-face gerado pelo CloudFront
    const normalized = sanitizeFontFamily(fontFamily.toLowerCase());
    return `'${normalized}', sans-serif`;
  };

  return `
    --typography-heading-font-family: ${formatFontFamily(typographies.HEADING.fontFamily)};
    --typography-heading-font-weight: ${sanitizeCssNumber(typographies.HEADING.fontWeight)};
    --typography-heading-font-size-desktop: ${sanitizeCssNumber(typographies.HEADING.fontSize.desktop)}px;
    --typography-heading-font-size-mobile: ${sanitizeCssNumber(typographies.HEADING.fontSize.mobile)}px;
    --typography-heading-line-height: ${sanitizeCssNumber(typographies.HEADING.lineHeight)};
    --typography-heading-letter-spacing: ${sanitizeCssLength(typographies.HEADING.letterSpacing)};

    --typography-subheading-font-family: ${formatFontFamily(typographies.SUB_HEADING.fontFamily)};
    --typography-subheading-font-weight: ${sanitizeCssNumber(typographies.SUB_HEADING.fontWeight)};
    --typography-subheading-font-size-desktop: ${sanitizeCssNumber(typographies.SUB_HEADING.fontSize.desktop)}px;
    --typography-subheading-font-size-mobile: ${sanitizeCssNumber(typographies.SUB_HEADING.fontSize.mobile)}px;
    --typography-subheading-line-height: ${sanitizeCssNumber(typographies.SUB_HEADING.lineHeight)};
    --typography-subheading-letter-spacing: ${sanitizeCssLength(typographies.SUB_HEADING.letterSpacing)};

    --typography-title-font-family: ${formatFontFamily(typographies.TITLE.fontFamily)};
    --typography-title-font-weight: ${sanitizeCssNumber(typographies.TITLE.fontWeight)};
    --typography-title-font-size-desktop: ${sanitizeCssNumber(typographies.TITLE.fontSize.desktop)}px;
    --typography-title-font-size-mobile: ${sanitizeCssNumber(typographies.TITLE.fontSize.mobile)}px;
    --typography-title-line-height: ${sanitizeCssNumber(typographies.TITLE.lineHeight)};
    --typography-title-letter-spacing: ${sanitizeCssLength(typographies.TITLE.letterSpacing)};

    --typography-text-font-family: ${formatFontFamily(typographies.TEXT.fontFamily)};
    --typography-text-font-weight: ${sanitizeCssNumber(typographies.TEXT.fontWeight)};
    --typography-text-font-size-desktop: ${sanitizeCssNumber(typographies.TEXT.fontSize.desktop)}px;
    --typography-text-font-size-mobile: ${sanitizeCssNumber(typographies.TEXT.fontSize.mobile)}px;
    --typography-text-line-height: ${sanitizeCssNumber(typographies.TEXT.lineHeight)};
    --typography-text-letter-spacing: ${sanitizeCssLength(typographies.TEXT.letterSpacing)};
  `;
}

/**
 * Gera CSS para design (button e card)
 */
function generateDesignCSS(styleguide: Styleguide): string {
  const { design } = styleguide;

  // Trata valores especiais para border-radius e border-width
  const buttonBorderRadius = design.button.borderRadius === 'pill'
    ? '9999px'
    : sanitizeCssLength(design.button.borderRadius);

  const buttonBorderWidth = design.button.borderWidth === 'none'
    ? '0'
    : sanitizeCssLength(design.button.borderWidth);

  const cardBorderRadius = sanitizeCssLength(design.card.borderRadius);

  const cardBorderWidth = design.card.borderWidth === 'none'
    ? '0'
    : sanitizeCssLength(design.card.borderWidth);

  return `
    --design-button-border-radius: ${buttonBorderRadius};
    --design-button-border-width: ${buttonBorderWidth};
    --design-card-border-radius: ${cardBorderRadius};
    --design-card-border-width: ${cardBorderWidth};
  `;
}

/**
 * Gera CSS para uma paleta específica
 */
function generatePaletteCSS(palette: PaletteConfig): string {
  return `
    --general-primary: ${sanitizeCssColor(palette.general.primary)};
    --general-background: ${sanitizeCssColor(palette.general.background)};
    --general-border: ${sanitizeCssColor(palette.general.border)};
    --text-title: ${sanitizeCssColor(palette.text.title)};
    --text-body: ${sanitizeCssColor(palette.text.body)};
    --cards-background: ${sanitizeCssColor(palette.cards.background)};
    --cards-title: ${sanitizeCssColor(palette.cards.title)};
    --cards-body: ${sanitizeCssColor(palette.cards.body)};
    --cards-border: ${sanitizeCssColor(palette.cards.border)};
    --buttons-background: ${sanitizeCssColor(palette.buttons.background)};
    --buttons-text: ${sanitizeCssColor(palette.buttons.text)};
    --buttons-border: ${sanitizeCssColor(palette.buttons.border)};
  `;
}

/**
 * Gera CSS completo para todas as paletas
 */
function generatePalettesCSS(styleguide: Styleguide): string {
  const { palettes } = styleguide;

  return Object.entries(palettes)
    .map(([name, palette]) => {
      const className = getPaletteClassName(name);
      return `.${className} {${generatePaletteCSS(palette)}}`;
    })
    .join('\n');
}

/**
 * Componente que injeta estilos do styleguide como CSS global
 */
export function StyleguideStyles({
  styleguide,
  includeFontFace = false,
}: StyleguideStylesProps) {
  if (!styleguide) {
    return null;
  }

  const fontFaceCss = includeFontFace ? resolveFontFaceCss(styleguide) : null;

  const cssContent = `
    ${fontFaceCss ?? ''}
    :root {
      ${generateTypographiesCSS(styleguide)}
      ${generateDesignCSS(styleguide)}
    }
    ${generatePalettesCSS(styleguide)}
  `;

  return (
    <style dangerouslySetInnerHTML={{ __html: cssContent }} />
  );
}
